/**
 * MobileTestInvite - Página para receber convite de teste comportamental
 * Verifica se candidato já tem resultado e oferece opção de enviar existente
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { testInvitesAPI, candidatesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  Brain,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  LogIn,
  UserPlus,
  ArrowRight,
  Send,
  RefreshCw,
  History,
  TrendingUp,
  Stethoscope
} from 'lucide-react';

const TIPO_TESTE_CONFIG = {
  disc: { 
    label: 'DISC', 
    color: 'text-violet-600', 
    bg: 'bg-violet-100',
    bgDark: 'bg-violet-900/30',
    borderDark: 'border-violet-500/30',
    description: 'Avaliação de perfil comportamental que analisa Dominância, Influência, Estabilidade e Conformidade.',
    duration: '10-15 minutos',
    route: '/m/disc'
  },
  g2bp: { 
    label: 'G2 Behavioral Profile', 
    color: 'text-indigo-600', 
    bg: 'bg-indigo-100',
    bgDark: 'bg-indigo-900/30',
    borderDark: 'border-indigo-500/30',
    description: 'Avaliação comportamental avançada G2eGente. Formato de escolha de palavras (MAIS/MENOS) para análise precisa do perfil DISC.',
    duration: '15-20 minutos',
    route: '/m/g2bp'
  },
  bigfive: { 
    label: 'Big Five', 
    color: 'text-blue-600', 
    bg: 'bg-blue-100',
    bgDark: 'bg-blue-900/30',
    borderDark: 'border-blue-500/30',
    description: 'Avaliação de personalidade baseada nos 5 grandes fatores: Abertura, Conscienciosidade, Extroversão, Amabilidade e Neuroticismo.',
    duration: '15-20 minutos',
    route: '/m/bigfive'
  }
};

const DISC_PROFILES = {
  D: { nome: 'Dominante', cor: 'text-red-500' },
  I: { nome: 'Influente', cor: 'text-yellow-500' },
  S: { nome: 'Estável', cor: 'text-green-500' },
  C: { nome: 'Conforme', cor: 'text-blue-500' }
};

export default function MobileTestInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [existingResult, setExistingResult] = useState(null);
  const [sendingExisting, setSendingExisting] = useState(false);
  const [startingTest, setStartingTest] = useState(false);

  useEffect(() => {
    validateInvite();
  }, [token]);

  // Verificar resultado existente quando usuário loga
  useEffect(() => {
    if (user && invite) {
      checkExistingResult();
    }
  }, [user, invite]);

  const validateInvite = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await testInvitesAPI.validate(token);
      setInvite(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Convite inválido ou expirado';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingResult = async () => {
    try {
      const response = await candidatesAPI.getMe();
      const candidate = response.data;
      
      if (invite.tipo_teste === 'disc' && candidate.disc_profile) {
        // Verificar histórico de DISC
        const historico = candidate.disc_historico || [];
        const ultimoResultado = historico.length > 0 
          ? historico[historico.length - 1] 
          : {
              perfil: candidate.disc_profile,
              detalhado: candidate.disc_detalhado,
              data: candidate.disc_detalhado?.created_at || new Date().toISOString()
            };
        
        setExistingResult({
          tipo: 'disc',
          perfil: candidate.disc_profile,
          detalhado: candidate.disc_detalhado,
          data: ultimoResultado.data,
          historico: historico
        });
      } else if (invite.tipo_teste === 'bigfive' && candidate.bigfive_result) {
        const historico = candidate.bigfive_historico || [];
        
        setExistingResult({
          tipo: 'bigfive',
          result: candidate.bigfive_result,
          data: candidate.bigfive_updated_at,
          historico: historico
        });
      } else if (invite.tipo_teste === 'g2bp' && candidate.g2bp_result) {
        const historico = candidate.g2bp_historico || [];
        
        setExistingResult({
          tipo: 'g2bp',
          result: candidate.g2bp_result,
          data: candidate.g2bp_updated_at,
          historico: historico
        });
      }
    } catch (error) {
      console.error('Erro ao verificar resultado existente:', error);
    }
  };

  const handleSendExisting = async () => {
    setSendingExisting(true);
    try {
      await testInvitesAPI.complete(token);
      toast.success('Resultado enviado com sucesso! A empresa foi notificada.');
      navigate('/m');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar resultado');
    } finally {
      setSendingExisting(false);
    }
  };

  const handleStartNewTest = () => {
    setStartingTest(true);
    const testConfig = TIPO_TESTE_CONFIG[invite.tipo_teste];
    sessionStorage.setItem('test_invite_token', token);
    navigate(testConfig.route);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Data não disponível';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return 'Data não disponível';
    }
  };

  const getResultAge = (dateStr) => {
    if (!dateStr) return null;
    try {
      const resultDate = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - resultDate) / (1000 * 60 * 60 * 24));
      if (diffDays < 30) return `${diffDays} dias`;
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 mês' : `${months} meses`;
    } catch {
      return null;
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-slate-400">Validando convite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Convite Inválido</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/m')} className="bg-cyan-600 hover:bg-cyan-700">
              Voltar para o App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const testConfig = TIPO_TESTE_CONFIG[invite?.tipo_teste];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800" data-testid="mobile-test-invite">
      <div className="px-4 py-8">
        <div className="max-w-md mx-auto">
          
          {/* Header Card */}
          <Card className="bg-slate-800 border-slate-700 mb-6">
            <CardContent className="p-6">
              <div className="text-center">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 ${testConfig?.bg}`}>
                  <Brain className={`w-10 h-10 ${testConfig?.color}`} />
                </div>
                
                <Badge className={`${testConfig?.bg} ${testConfig?.color} mb-3`}>
                  {testConfig?.label}
                </Badge>
                
                <h1 className="text-2xl font-bold text-white mb-2">
                  Convite para Teste Comportamental
                </h1>
                
                <p className="text-slate-400 mb-4">
                  Você foi convidado(a) por <span className="text-cyan-400 font-medium">{invite?.company_name}</span> para responder uma avaliação comportamental.
                </p>

                <div className="p-4 bg-slate-700/50 rounded-xl mb-4">
                  <p className="text-slate-300 text-sm">
                    <User className="w-4 h-4 inline mr-2" />
                    Destinatário: <span className="font-medium">{invite?.nome_destinatario}</span>
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    CPF: {invite?.cpf_destinatario}
                  </p>
                </div>

                {/* Psicólogo responsável - exibir apenas se houver */}
                {invite?.psicologo && (
                  <div className="p-4 bg-emerald-900/30 border border-emerald-500/30 rounded-xl mb-4">
                    <p className="text-emerald-300 text-sm flex items-center gap-2">
                      <Stethoscope className="w-4 h-4" />
                      <span>Psicólogo(a) Responsável</span>
                    </p>
                    <p className="text-white font-medium mt-1">
                      {invite.psicologo.nome}
                      {invite.psicologo.crp && (
                        <span className="text-emerald-400 text-sm ml-2">
                          CRP: {invite.psicologo.crp}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resultado Existente - Se usuário logado e tem resultado */}
          {user && existingResult && (
            <Card className={`mb-6 ${testConfig?.bgDark} border ${testConfig?.borderDark}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">
                      Você já possui este teste!
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Preenchido em {formatDate(existingResult.data)}
                      {getResultAge(existingResult.data) && (
                        <span className="text-slate-500"> (há {getResultAge(existingResult.data)})</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Preview do Resultado DISC */}
                {existingResult.tipo === 'disc' && (
                  <div className="p-4 bg-slate-800/50 rounded-xl mb-4">
                    <p className="text-slate-400 text-xs mb-2">Seu perfil atual:</p>
                    <div className="flex items-center gap-3">
                      <span className={`text-3xl font-bold ${DISC_PROFILES[existingResult.perfil?.[0]]?.cor || 'text-white'}`}>
                        {existingResult.perfil}
                      </span>
                      <span className="text-white">
                        - {DISC_PROFILES[existingResult.perfil?.[0]]?.nome || 'Perfil'}
                      </span>
                    </div>
                    {existingResult.detalhado?.pontos_fortes && (
                      <p className="text-slate-400 text-sm mt-2">
                        <strong className="text-slate-300">Pontos fortes:</strong> {existingResult.detalhado.pontos_fortes.slice(0, 2).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Preview do Resultado Big Five */}
                {existingResult.tipo === 'bigfive' && existingResult.result && (
                  <div className="p-4 bg-slate-800/50 rounded-xl mb-4">
                    <p className="text-slate-400 text-xs mb-2">Seu perfil atual:</p>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-blue-400">
                        {existingResult.result.dominant}
                      </span>
                      {existingResult.result.secondary && (
                        <span className="text-slate-400">
                          / {existingResult.result.secondary}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Histórico se houver */}
                {existingResult.historico && existingResult.historico.length > 1 && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-slate-800/30 rounded-lg">
                    <History className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-xs">
                      {existingResult.historico.length} avaliações no histórico
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="ml-auto text-xs text-cyan-400"
                      onClick={() => navigate('/m/evolucao-comportamental')}
                    >
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Ver evolução
                    </Button>
                  </div>
                )}

                {/* Botões de Ação */}
                <div className="space-y-3">
                  <Button 
                    onClick={handleSendExisting}
                    disabled={sendingExisting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                  >
                    {sendingExisting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    Enviar resultado ao solicitante
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleStartNewTest}
                    disabled={startingTest}
                    className="w-full h-12 border-slate-600 text-white hover:bg-slate-700"
                  >
                    {startingTest ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-5 h-5 mr-2" />
                    )}
                    Refazer teste (novo resultado)
                  </Button>
                </div>

                <p className="text-center text-slate-500 text-xs mt-4">
                  Ao refazer, o novo resultado será adicionado ao seu histórico
                </p>
              </CardContent>
            </Card>
          )}

          {/* Test Info - Mostrar apenas se não tem resultado existente ou não está logado */}
          {(!user || !existingResult) && (
            <Card className="bg-slate-800 border-slate-700 mb-6">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Sobre o Teste</h3>
                <p className="text-slate-400 text-sm mb-4">
                  {testConfig?.description}
                </p>
                
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>Duração: {testConfig?.duration}</span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-amber-900/30 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                    <p className="text-amber-300 text-xs">
                      Este convite é pessoal e intransferível. Expira em{' '}
                      {new Date(invite?.expires_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Area - Para usuários não logados */}
          {!user && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Acesse sua Conta</h3>
                  <p className="text-slate-400 text-sm">
                    Para responder o teste, você precisa estar logado com uma conta vinculada ao CPF informado.
                  </p>
                </div>

                <div className="space-y-3">
                  <Link to={`/m/login?redirect=/m/test-invite/${token}`}>
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 h-12">
                      <LogIn className="w-5 h-5 mr-2" />
                      Já tenho conta - Entrar
                    </Button>
                  </Link>
                  
                  <Link to={`/m/register?redirect=/m/test-invite/${token}`}>
                    <Button variant="outline" className="w-full h-12 border-slate-600 text-white hover:bg-slate-700">
                      <UserPlus className="w-5 h-5 mr-2" />
                      Criar Conta Rápida
                    </Button>
                  </Link>
                </div>

                <p className="text-center text-slate-500 text-xs mt-4">
                  O cadastro é rápido e você só precisa informar seus dados básicos.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Action Area - Para usuários logados SEM resultado existente */}
          {user && !existingResult && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-emerald-300 font-medium text-sm">Você está logado(a)</p>
                    <p className="text-emerald-400/70 text-xs">{user.email}</p>
                  </div>
                </div>

                <Button 
                  onClick={handleStartNewTest}
                  disabled={startingTest}
                  className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-lg"
                >
                  {startingTest ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-5 h-5 mr-2" />
                  )}
                  Iniciar Teste
                </Button>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}
